import React, {useContext, useEffect, useReducer, useRef, useState} from "react";
import useTooltip from "../hooks/useTooltip";
import {ContentContext} from "../common/ContentContext";
import {BrandContext} from "../common/BrandContext";
import axios from "../common/axios";
import TabAccessibilityWrap from "../nav/TabAccessibilityWrap";
import CircularLoader from "../common/CircularLoader";
import DynamicContent from "../nav/DynamicContent";
import AlgebraKitBlock from "../common/AlgebraKitBlock";
import {UserContext} from "../common/UserContext";
import GenericDialog from "../common/GenericDialog";
import NavigationRightDevelopment from "../nav/NavigationRightDevelopment";
import PreviousNextLessonDevelopment from "../nav/PreviousNextLessonDevelopment";
import LessonButtons from "./LessonButtons";
import WalkthroughsAndQuestions from "./WalkthroughsAndQuestions";
import PracticeQuestions from "../questions/PracticeQuestion";
import BookmarkLink from "../bookmarks/BookmarkLink";
import {BookmarkAddIcon} from "../common/icons";
import {useScreenMode} from "../common/context/ScreenModeContext";

const TabButtons = React.memo(({tabs, activeTab, smallScreenMode, hideSingleTabTitle, tabRefs, handleChangeTab}) => {
    let displayedTabs = [];
    tabs.map(tab => {
        if (tab.hasContent && !tab.childTab && !tab.hidden && (!tab.leftColumn || smallScreenMode) && (!tab.singleTab || !hideSingleTabTitle)) {
            displayedTabs.push(tab);
        }
    });
    if (displayedTabs.length <= 1) return '';
    return (
        <>
            {displayedTabs.map((tab, index) => {
                const isActive = activeTab && tab.tabId === activeTab.tabId;
                const tabId = `lesson-tab-${tab.tabId}`;
                return (
                    <button type="button"
                         className={"js-gen-tab ch-nav-item " + (isActive ? " sel" : "")}
                         ref={(el) => tabRefs.current.set(tab.tabName, el)}
                         key={tab.tabId}
                         id={tabId}
                         tabIndex={isActive ? 0 : -1}
                         role="tab"
                         aria-selected={isActive}
                         aria-controls="lesson-tabpanel"
                         data-hash={tab.hashName}
                         data-tab-id={tab.tabId}
                         data-tab-brand-id={tab.tabBrandId}
                         onClick={() => handleChangeTab(tab.hashName)}
                         dangerouslySetInnerHTML={{__html: tab.tabName}}></button>
                )
            })}
        </>
    )
});

const Lesson = props => {
    const {smallScreenMode} = useScreenMode();
    const {content, updateCssStylesAndClasses} = useContext(ContentContext);
    const brandConfig = useContext(BrandContext);
    const {user} = useContext(UserContext);
    const [staticTab, setStaticTab] = useState();
    const [bookmarkDialogOpen, setBookmarkDialogOpen] = useState(false);
    const tabRefs = useRef(new Map());
    const userActivatedTab = useRef(false);

    useTooltip(".ch-lesson-width");

    const tabComponents = {
        DynamicContent: DynamicContent,
        AlgebraKitBlock: AlgebraKitBlock,
        WalkthroughsAndQuestions: WalkthroughsAndQuestions,
        PracticeQuestions: PracticeQuestions
    }

    const ACTIONS = {
        LOAD_TABS: "loadTabs",
        UPDATE_ACTIVE_TAB: "updateActiveTab",
        UPDATE_TAB_LOADED: "updateTabLoaded"
    }

    const buildContentComponent = (loadTab, params) => {
        if (loadTab == undefined) {
            return;
        }

        let compiledUrl = loadTab.url;
        if (compiledUrl) {

            if(params) {
                params.map((param, index) => {
                    const re = new RegExp('{#' + (index + 1) + '}', 'g');
                    compiledUrl = compiledUrl.replace(re, param);
                });
            }

            const re = new RegExp('{#1}', 'g');
            compiledUrl = compiledUrl.replace(re, '');

            if (!compiledUrl) {
                compiledUrl = loadTab.url;
            }
        }
        loadTab.compiledUrl = compiledUrl;

        const contentCallback = () => {
            if (loadTab && loadTab.loadFunction && typeof window[loadTab.loadFunction] === "function") {
                try {
                    window[loadTab.loadFunction](params, () => {
                        buildPageMarkers();
                        dispatch({type: ACTIONS.UPDATE_TAB_LOADED, payload: {loading: false}});
                    });
                } catch (e) {
                    console.error('Error running lesson scripts', e);
                    dispatch({type: ACTIONS.UPDATE_TAB_LOADED, payload: {loading: false}});
                }
            } else {
                dispatch({type: ACTIONS.UPDATE_TAB_LOADED, payload: {loading: false}});
            }
            updateCssStylesAndClasses(content);
        }

        loadTab.contentCallback = contentCallback;
    }

    const reducer = (state, action) => {
        if (action.type === ACTIONS.LOAD_TABS) {
            return Object.assign({}, state, {
                ...defaultState,
                tabs: action.payload.tabs
            });
        } else if (action.type === ACTIONS.UPDATE_ACTIVE_TAB) {
            buildContentComponent(action.payload.activeTab, action.payload.hash.params);
            return Object.assign({}, state, {
                hash: action.payload.hash,
                activeTab: action.payload.activeTab.layoutAttribute === "LEFT_COLUMN" ? state.activeTab : action.payload.activeTab,
                activeTabSmallScreen: action.payload.activeTab,
                tabLoading: action.payload.activeTab == undefined || action.payload.activeTab.tabComponent == 'DynamicContent',
            });
        } else if (action.type === ACTIONS.UPDATE_TAB_LOADED) {
            return Object.assign({}, state, {
                tabLoading: action.payload.loading
            });
        } else {
            return state;
        }
    }

    const defaultState = {
        singlePanel: !brandConfig.twoColumnLayout && !user.supplementary,
        hash: {},
        tabs: [],
        tabLoading: true,
        activeTab: null,
        activeTabSmallScreen: null,
    }
    const [state, dispatch] = useReducer(reducer, defaultState);

    const loadConceptTabs = () => {
        axios.get("/web/ui/tab/all/me")
            .then(response => {
                buildTabsObjects(response.data);
            }).catch(error => {
                console.error("Error loading tabs", error);
            });
    }

    const buildTabsObjects = tabs => {
        let newTabs = [];
        tabs.map(tab => {
            const buildHashArray = hash => {
                if (hash) {
                    return hash.split(".").map(hashSegment => {
                        if (hashSegment != "#" && hashSegment != "") return hashSegment;
                    });
                } else {
                    return null;
                }
            }
            newTabs.push(Object.assign({}, tab, {
                hash: buildHashArray(tab.hashName)
            }));
        });
        dispatch({type: ACTIONS.LOAD_TABS, payload: {tabs: newTabs}});
    }

    function loadStaticComponent() {
        const staticTab = state.tabs.find(tab => tab.layoutAttribute === "LEFT_COLUMN");
        if(staticTab) {
            buildContentComponent(staticTab);
            setStaticTab(staticTab);
        } else {
            setStaticTab(undefined);
        }
    }

    function loadTabComponent() {
        const  stateActiveTab = smallScreenMode ? state.activeTabSmallScreen : state.activeTab;
        const urlHash = buildHash(window.location.hash, "/");

        let newActiveTab = findTabByHash(urlHash);
        if (stateActiveTab) {
            if (!newActiveTab || newActiveTab.tabId !== stateActiveTab.tabId) {
                updateTabHash(stateActiveTab.hashName);
            }
        } else {
            if (newActiveTab && !(!smallScreenMode && newActiveTab.layoutAttribute === "LEFT_COLUMN")) {
                const hashObject = buildHashObject(urlHash, newActiveTab);

                dispatch({type: ACTIONS.UPDATE_ACTIVE_TAB, payload: {hash: hashObject, activeTab: newActiveTab}});
            } else {
                newActiveTab = state.tabs.find(tab => tab.defaultTab);
                if (!newActiveTab && state.tabs.length > 0) {
                    newActiveTab = state.tabs[0];
                }
                if (newActiveTab) {
                    updateTabHash(newActiveTab.hashName);
                }
            }
        }
    }

    const buildHashObject = (hash, tab) => {
        return {
            urlHash: hash.join('/'),
            tabHash: tab ? tab.hash : undefined,
            params: tab && tab.hash ? hash.slice(tab.hash.length) : undefined
        };
    }

    function findTabByHash(hash) {
        let matchedTab;
        let bestMatch = 0;
        state.tabs.map(tab => {
            let matches = 0;
            let index = 0;
            for (const hashSection of tab.hash) {
                if (hashSection !== hash[index]) return false;
                if (hash.length >= tab.hash.length) matches++;
                index++;
            }
            if (matches > bestMatch) {
                matchedTab = tab;
                bestMatch = matches;
            }
        });
        return matchedTab;
    }

    const buildHash = (hash, separator) => {
        let newHash = [];
        hash.split(separator).map(hashSegment => {
            if (hashSegment != "#" && hashSegment != "") {
                newHash.push(hashSegment.replace('#', ''));
            }
        });
        return newHash;
    }

    const updateTabHash = hash => {
        let locationHash = hash.replaceAll(".", "/")
        if (locationHash == 'scorcher') {
            locationHash += '/world';
        }
        window.location.hash = locationHash;
    }

    const handleHashChange = () => {
        const urlHash = buildHash(window.location.hash, "/");
        const newActiveTab = findTabByHash(urlHash);
        if(!newActiveTab) {
            return;
        }

        const hashObject = buildHashObject(urlHash, newActiveTab);

        if (state.hash.urlHash != hashObject.urlHash) {
            dispatch({type: ACTIONS.UPDATE_ACTIVE_TAB, payload: {hash: hashObject, activeTab: newActiveTab}});
        }
    };

    const handleChangeTab = tabHash => {
        userActivatedTab.current = true;
        updateTabHash(tabHash);
    }

    const getActiveTab  = () => {
        return smallScreenMode ? state.activeTabSmallScreen : state.activeTab;
    }

    useEffect(() => {
        if (content.currentConcept) {
            loadConceptTabs(content.currentConcept.conceptId);
        }
    }, [content.currentConcept]);

    useEffect(() => {
        if (state.tabs.length > 0) {
            if (!state.singlePanel) {
                loadStaticComponent();
            } else {
                setStaticTab(undefined);
            }
            loadTabComponent();
        } else {
            dispatch({type: ACTIONS.UPDATE_TAB_LOADED, payload: {loading: false}});
        }
    }, [state.tabs, smallScreenMode]);

    useEffect(() => {
        if (userActivatedTab.current && state.activeTab && !state.tabLoading) {
            const tabRef = tabRefs.current.get(state.activeTab.tabName);
            if (tabRef) {
                tabRef.focus();
                userActivatedTab.current = false; // Reset the flag after focusing
            }
        }
    }, [state.activeTab, state.tabLoading]);

    useEffect(() => {
        window.addEventListener('hashchange', handleHashChange);
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, [state, smallScreenMode]);

    const activeTab = getActiveTab();
    const ContentComponent = activeTab ? tabComponents[activeTab.tabComponent]: undefined;
    const StaticComponent = staticTab ? tabComponents[staticTab.tabComponent]: undefined;

    if(!content.currentConcept) {
        return <div className="ch-error-text">No lesson selected</div>;
    }

    return (
        <>
            {state.tabs && state.tabs.length > 0 && (
                <div className="ch-tab-bar js-lesson-tab-bar">
                    <div className="ch-tab-scroller">
                        {!state.singlePanel && !smallScreenMode ? (
                            <div className="ch-tab-scroller-area ch-tab-scroller-two-panel">
                                <div className="ch-tab-scroller-content ch-lesson-content-two-panel margin-r--m">
                                    <LessonButtons activeTab={activeTab} setBookmarkDialogOpen={setBookmarkDialogOpen} singlePanel={false} />
                                </div>
                                <TabAccessibilityWrap aria-label="Lesson Navigation" className="ch-lesson-content-two-panel margin-l--m" orientation="horizontal">
                                    <div role="tablist" aria-label="lesson tabs" aria-orientation="horizontal" className="ch-tab-scroller-content ch-nav-group">
                                        <TabButtons
                                            tabs={state.tabs}
                                            activeTab={activeTab}
                                            smallScreenMode={smallScreenMode}
                                            hideSingleTabTitle={brandConfig.hideSingleTabTitle}
                                            tabRefs={tabRefs}
                                            handleChangeTab={handleChangeTab}
                                        />
                                    </div>
                                </TabAccessibilityWrap>
                            </div>
                        ) : (
                            <div className="ch-tab-scroller-area">
                                <TabAccessibilityWrap aria-label="Lesson Navigation" orientation="horizontal">
                                    <div role="tablist" aria-label="lesson tabs" aria-orientation="horizontal" className="ch-tab-scroller-content ch-nav-group">
                                        <TabButtons
                                            tabs={state.tabs}
                                            activeTab={activeTab}
                                            smallScreenMode={smallScreenMode}
                                            hideSingleTabTitle={brandConfig.hideSingleTabTitle}
                                            tabRefs={tabRefs}
                                            handleChangeTab={handleChangeTab}
                                        />
                                    </div>
                                </TabAccessibilityWrap>
                                <LessonButtons activeTab={activeTab} setBookmarkDialogOpen={setBookmarkDialogOpen} singlePanel={true} smallScreenMode={smallScreenMode} />
                            </div>
                        )}
                    </div>
                </div>
            )}
            <div role="tabpanel"
                 id="lesson-tabpanel"
                 aria-labelledby={activeTab ? `lesson-tab-${activeTab.tabId}` : undefined}
                 className={'ch-lesson-wrapper' + (brandConfig.useDevelopmentNavigation && brandConfig.centerLessonContent ? ' ch-lesson-center-content' : '')}>
                <div className={'ch-lesson-width'}>
                    <div className="lesson-content js-lessonContent flex" data-concept-id={content && content.currentConcept ? content.currentConcept.conceptId: ''}>
                        {!state.singlePanel && !smallScreenMode && (
                            <div className="ch-lesson-content-two-panel flex margin-r--m">
                                <CircularLoader visible={!staticTab || !StaticComponent || state.tabLoading}/>
                                {staticTab && StaticComponent && (
                                    <StaticComponent url={staticTab.compiledUrl} className={staticTab.tabClass ? staticTab.tabClass : ''} style={{width: '100%'}} entityId={content.currentConcept.conceptId} updateHash={false} callback={staticTab.contentCallback} />
                                )}
                            </div>
                        )}
                        <div className={"ch-lesson ch-lesson-content" + (brandConfig.useContentTemplate ? ' ch-lesson-template' : '') + (!state.singlePanel && !smallScreenMode ? " ch-lesson-content-two-panel flex margin-l--m" : "")}>
                            <CircularLoader visible={state.tabLoading} displayInElement={true}/>
                            <div id="generalContent" className={'ch-lesson-content-body' + (state.tabLoading ? ' hide' : '')}>
                                <>
                                    {brandConfig.showContentTitle && activeTab && activeTab.contentTitle && (
                                        <div className="text-heading-unit">
                                            {content.currentConcept.conceptName}
                                        </div>
                                    )}
                                    {activeTab && ContentComponent &&
                                            <ContentComponent url={activeTab.compiledUrl} className={activeTab.tabClass ? activeTab.tabClass : ''} style={{width: '100%'}} hash={state.hash} updateHash={false} callback={activeTab.contentCallback} />
                                    }
                                </>
                            </div>
                        </div>
                    </div>
                </div>
                {
                    brandConfig.useDevelopmentNavigation && !smallScreenMode && !state.tabLoading && state.tabs.length > 0 &&
                        <NavigationRightDevelopment smallScreenMode={smallScreenMode} compressed={false} callback={null} />
                }
            </div>
            {brandConfig.useDevelopmentNavigation && !state.tabLoading && state.tabs.length > 0 &&
                <PreviousNextLessonDevelopment />
            }
            {state?.activeTab &&
                <GenericDialog
                    title="Add Bookmark"
                    icon={<BookmarkAddIcon fill={'#ffffff'} />}
                    open={bookmarkDialogOpen}
                    onClose={() => setBookmarkDialogOpen(false)}
                    fullWidth
                    buttons={[
                        {
                            name: 'Close',
                            callback: async () => setBookmarkDialogOpen(false)
                        }
                    ]}
                >
                    <BookmarkLink conceptId={content.currentConcept.conceptId} urlHash={state.hash.urlHash} tabBrandId={state.activeTab.tabBrandId} resourceType={state.activeTab.resourceType}/>
                </GenericDialog>
            }
        </>
    )
}

export default Lesson;